// src/components/manager-v2/flows/TeamAutomationRules.js
import React, { useState, useEffect } from 'react';
import {
    Zap, Plus, ArrowRight, ToggleLeft, ToggleRight, Trash2
} from 'lucide-react';
import { useFlowAutomation, generateId } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import flowAutomationService from '../../../services/flowAutomation.service';

const TEAM_TRIGGERS = [
    { value: 'leave_requested', label: 'Team member requests leave' },
    { value: 'attendance_regularization', label: 'Team member attendance issue' },
    { value: 'probation_ending', label: 'Team member probation ending' },
    { value: 'birthday', label: 'Team member birthday' },
    { value: 'work_anniversary', label: 'Team member anniversary' },
];

const TEAM_ACTIONS = [
    { value: 'send_notification', label: 'Send me a notification' },
    { value: 'create_task', label: 'Create a task' },
];

const TeamAutomationRules = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { saveWorkflow, initNewWorkflow } = useFlowAutomation();

    const [rules, setRules] = useState([]);

    // Load rules from backend on mount
    useEffect(() => {
        const loadRules = async () => {
            try {
                const result = await flowAutomationService.getTeamWorkflows();
                if (result.workflows?.length) {
                    const loaded = result.workflows
                        .filter(wf => wf.type === 'task_automation')
                        .map(wf => {
                            const triggerStep = wf.steps?.find(s => s.type === 'trigger');
                            const actionStep = wf.steps?.find(s => s.type === 'action');
                            return {
                                id: wf.id,
                                name: wf.name,
                                trigger: triggerStep?.config?.event || 'manual',
                                action: actionStep?.config?.action_type || 'send_notification',
                                enabled: wf.status === 'active',
                            };
                        });
                    setRules(loaded);
                }
            } catch (err) {
                console.error('Error loading team automation rules:', err);
            }
        };
        loadRules();
    }, []);
    const [showForm, setShowForm] = useState(false);
    const [newRule, setNewRule] = useState({
        name: '',
        trigger: 'leave_requested',
        action: 'send_notification',
    });
    const [saving, setSaving] = useState(false);

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

    const handleCreate = async () => {
        if (!newRule.name.trim()) return;
        setSaving(true);

        const workflow = initNewWorkflow({
            name: newRule.name,
            type: 'task_automation',
            scope: 'team',
            steps: [
                {
                    id: generateId('step'),
                    type: 'trigger',
                    label: TEAM_TRIGGERS.find(t => t.value === newRule.trigger)?.label || 'Trigger',
                    config: { event: newRule.trigger, filters: {} },
                    position: { x: 300, y: 80 },
                },
                {
                    id: generateId('step'),
                    type: 'action',
                    label: TEAM_ACTIONS.find(a => a.value === newRule.action)?.label || 'Action',
                    config: { action_type: newRule.action, params: { title: newRule.name, message: '' } },
                    position: { x: 300, y: 230 },
                },
                {
                    id: generateId('step'),
                    type: 'end',
                    label: 'End',
                    config: {},
                    position: { x: 300, y: 380 },
                },
            ],
        });

        workflow.connections = [
            { id: generateId('conn'), from_step_id: workflow.steps[0].id, to_step_id: workflow.steps[1].id },
            { id: generateId('conn'), from_step_id: workflow.steps[1].id, to_step_id: workflow.steps[2].id },
        ];

        await saveWorkflow(workflow);
        setSaving(false);

        const updated = [...rules, { ...newRule, id: workflow.id, enabled: true }];
        setRules(updated);
        saveRulesToStorage(updated);
        setNewRule({ name: '', trigger: 'leave_requested', action: 'send_notification' });
        setShowForm(false);
    };

    const saveRulesToStorage = (data) => {
        try { localStorage.setItem('hrportal_team_automation_rules', JSON.stringify(data)); } catch {}
    };

    const toggleRule = (id) => {
        const updated = rules.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r);
        setRules(updated);
        saveRulesToStorage(updated);
    };

    const deleteRule = (id) => {
        const updated = rules.filter(r => r.id !== id);
        setRules(updated);
        saveRulesToStorage(updated);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className={`text-sm flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Set up simple automations that trigger when team events occur
                </p>
                <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 flex-shrink-0"
                >
                    <Plus className="w-4 h-4" />
                    New Rule
                </button>
            </div>

            {/* New Rule Form */}
            {showForm && (
                <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="space-y-3">
                        <div>
                            <label className={labelClass}>Rule Name</label>
                            <input
                                type="text"
                                value={newRule.name}
                                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                                className={inputClass}
                                placeholder="e.g., Notify me when leave > 3 days"
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>When...</label>
                                <select
                                    value={newRule.trigger}
                                    onChange={(e) => setNewRule({ ...newRule, trigger: e.target.value })}
                                    className={inputClass}
                                >
                                    {TEAM_TRIGGERS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Then...</label>
                                <select
                                    value={newRule.action}
                                    onChange={(e) => setNewRule({ ...newRule, action: e.target.value })}
                                    className={inputClass}
                                >
                                    {TEAM_ACTIONS.map(a => (
                                        <option key={a.value} value={a.value}>{a.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={() => setShowForm(false)}
                            className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={saving || !newRule.name.trim()}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                        >
                            {saving ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </div>
            )}

            {/* Rules List */}
            {rules.length === 0 && !showForm ? (
                <div className={`flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed ${
                    isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50/50'
                }`}>
                    <Zap className={`w-8 h-8 mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                    <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>No rules yet</h3>
                    <p className={`text-sm text-center max-w-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Create rules like "Notify me when a team member requests leave"
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {rules.map((rule) => (
                        <div
                            key={rule.id}
                            className={`flex items-center justify-between p-4 rounded-xl border ${
                                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <Zap className={`w-4 h-4 ${rule.enabled ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <div>
                                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{rule.name}</p>
                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {TEAM_TRIGGERS.find(t => t.value === rule.trigger)?.label} → {TEAM_ACTIONS.find(a => a.value === rule.action)?.label}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => toggleRule(rule.id)}
                                    aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
                                    title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                                    className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                                >
                                    {rule.enabled ? (
                                        <ToggleRight className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <ToggleLeft className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                    )}
                                </button>
                                <button
                                    onClick={() => deleteRule(rule.id)}
                                    aria-label="Delete rule"
                                    title="Delete rule"
                                    className={`p-1 rounded text-red-500 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TeamAutomationRules;
